'use client';
import { Stack, StackProps } from '@mantine/core';
import { NameValuePair } from '@/app/assets/details/atoms/NameValuePair';
import { CollapsibleSection } from '@/app/components/shared/viewer/CollapsibleSection';
import { RpdInfo } from '@/types/rawModel';
import { sumUndefinedNumbers } from '@/utils/sumUndefinedNumber';

interface SourceModelInformationProps extends StackProps {
  rpdInfo: RpdInfo;
}

export function SourceModelInformation({
  rpdInfo,
  ...props
}: SourceModelInformationProps) {
  return (
    <CollapsibleSection title='Model information'>
      <Stack gap={4} {...props}>
        <NameValuePair name='Bounding box size' value={rpdInfo.bbSize} />
        <NameValuePair name='Meshes' value={rpdInfo.numMeshes} />
        <NameValuePair name='Mesh Nodes' value={rpdInfo.numNodes} />
        <NameValuePair name='Vertices' value={rpdInfo.numVertices} />
        <NameValuePair
          name='Faces'
          value={sumUndefinedNumbers(rpdInfo.numTriangles, rpdInfo.numQuads)}
        />
        <NameValuePair name='Materials' value={rpdInfo.numMaterials} />
        <NameValuePair name='UVs' value={rpdInfo.hasUVs} />
        <NameValuePair name='Vertex Colors' value={rpdInfo.hasVertexColors} />
        <NameValuePair
          name='Total Number of Texture Maps'
          value={sumUndefinedNumbers(
            rpdInfo.numTSNormalMaps,
            rpdInfo.numOSNormalMaps,
            rpdInfo.numBaseColorMaps,
            rpdInfo.numMetallicMaps,
            rpdInfo.numRoughnessMaps,
            rpdInfo.numOcclusionMaps,
            rpdInfo.numDisplacmentMaps,
            rpdInfo.numEmissiveMaps
          )}
        />
        <NameValuePair
          name='Normal Map'
          value={rpdInfo.hasTSNormalMap || rpdInfo.hasOSNormalMap}
        />
        <NameValuePair
          name='BaseColor/albedo/Diffuse Map'
          value={rpdInfo.hasBaseColorMap}
        />
        <NameValuePair
          name='Roughness/Gloss Map'
          value={rpdInfo.hasMetallicMap}
        />
        <NameValuePair name='Occlusion Map' value={rpdInfo.hasOcclusionMap} />
        <NameValuePair
          name='Displacement Map'
          value={rpdInfo.hasDisplacmentMap}
        />
        <NameValuePair name='Emissive Map' value={rpdInfo.hasEmissiveMap} />
        <NameValuePair
          name='Skinned Animations'
          value={rpdInfo.hasSkinnedAnimations}
        />
      </Stack>
    </CollapsibleSection>
  );
}
